import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Dashboard,
  People,
  Home,
  Analytics,
  Person,
  Logout,
  AdminPanelSettings,
  Assessment,
  Gavel,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export const EmployeeNavigation: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, userProfile, signOut, isLoading } = useAuth();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      handleMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const employeeNavigationItems = [
    { label: 'Dashboard', href: '/admin/dashboard', icon: <Dashboard /> },
    { label: 'User Management', href: '/admin/users', icon: <People /> },
    { label: 'Listing Moderation', href: '/admin/listings', icon: <Home /> },
    { label: 'Financial Reports', href: '/admin/financial', icon: <Analytics /> },
    { label: 'System Reports', href: '/admin/reports', icon: <Assessment /> },
    { label: 'Moderation Log', href: '/admin/moderation', icon: <Gavel /> },
  ];

  const drawer = (
    <Box sx={{ width: 280 }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <AdminPanelSettings sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            BnBCart Admin
          </Typography>
        </Box>
        {userProfile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Avatar 
              src={userProfile.photoUrl} 
              sx={{ width: 32, height: 32, mr: 1 }}
            >
              {userProfile.name?.charAt(0) || userProfile.email?.charAt(0)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {userProfile.name || 'Employee'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                  label="Employee" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <List>
        {employeeNavigationItems.map((item) => (
          <ListItem 
            key={item.label} 
            component={Link} 
            to={item.href}
            sx={{
              backgroundColor: location.pathname === item.href ? 'primary.main' : 'transparent',
              color: location.pathname === item.href ? 'white' : 'inherit',
              '&:hover': {
                backgroundColor: location.pathname === item.href ? 'primary.dark' : 'action.hover',
              },
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
            }}
          >
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
              {item.icon}
            </Box>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" sx={{ backgroundColor: 'primary.dark', borderRadius: 0, zIndex: (theme) => theme.zIndex.appBar }}>
        <Toolbar>
          {isMobile && isAuthenticated && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <AdminPanelSettings sx={{ mr: 1 }} />
            <Typography
              variant="h6"
              component={Link}
              to="/admin/dashboard"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                fontWeight: 'bold',
              }}
            >
              BnBCart Admin
            </Typography>
          </Box>

          {!isMobile && isAuthenticated && (
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              {employeeNavigationItems.slice(0, 4).map((item) => (
                <Button
                  key={item.label}
                  color="inherit"
                  component={Link}
                  to={item.href}
                  startIcon={item.icon}
                  size="small"
                  sx={{
                    backgroundColor: location.pathname === item.href ? 'rgba(255,255,255,0.15)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {isAuthenticated ? (
            <Box>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {userProfile?.name || 'Employee'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {userProfile?.email}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip 
                      label="Employee Access" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 18 }}
                    />
                  </Box>
                </Box>
                <MenuItem component={Link} to="/admin/profile" onClick={handleMenuClose}>
                  <Person sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          ) : null}
        </Toolbar>
      </AppBar>

      {isMobile && isAuthenticated && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {drawer}
        </Drawer>
      )}
    </>
  );
};